import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import api from '../utils/api';
import './InvoiceDetail.css';

function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await api.get(`/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await api.get(`/invoices/${id}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendNotification = async (channels) => {
    try {
      setSending(true);
      await api.post(`/invoices/${id}/send`, { channels });
      toast.success('Notifications sent successfully');
      fetchInvoice(); // Refresh to see notification history
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error(error.response?.data?.message || 'Failed to send notifications');
    } finally {
      setSending(false);
    }
  };

  const handlePayment = () => {
    // Navigate to payment page or open payment modal
    navigate(`/payments/${invoice.order._id}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading invoice...</div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <div className="error">Invoice not found</div>
      </Layout>
    );
  }

  const isBuyer = invoice.buyer._id === localStorage.getItem('userId');
  const isSeller = invoice.seller._id === localStorage.getItem('userId');

  return (
    <Layout>
      <div className="invoice-detail">
        <div className="invoice-header">
          <div className="invoice-title">
            <h1>Invoice {invoice.invoiceNumber}</h1>
            <span className={`status-badge ${invoice.status}`}>
              {invoice.status.toUpperCase()}
            </span>
          </div>

          <div className="invoice-actions">
            <button
              className="btn btn-primary"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? 'Downloading...' : '📥 Download PDF'}
            </button>

            {isSeller && (
              <div className="send-options">
                <button
                  className="btn btn-secondary"
                  onClick={() => handleSendNotification(['email'])}
                  disabled={sending}
                >
                  📧 Email
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleSendNotification(['sms'])}
                  disabled={sending}
                >
                  📱 SMS
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleSendNotification(['whatsapp'])}
                  disabled={sending}
                >
                  💬 WhatsApp
                </button>
              </div>
            )}

            {isBuyer && invoice.paymentDetails.status !== 'paid' && (
              <button className="btn btn-success" onClick={handlePayment}>
                💳 Pay Now
              </button>
            )}
          </div>
        </div>

        <div className="invoice-content">
          <div className="invoice-info-grid">
            <div className="info-card">
              <h3>From: {invoice.sellerDetails.businessName}</h3>
              {invoice.sellerDetails.address?.street && <p>{invoice.sellerDetails.address.street}</p>}
              {(invoice.sellerDetails.address?.city || invoice.sellerDetails.address?.state || invoice.sellerDetails.address?.pincode) && (
                <p>
                  {[invoice.sellerDetails.address?.city, invoice.sellerDetails.address?.state, invoice.sellerDetails.address?.pincode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              <p>📞 {invoice.sellerDetails.phone}</p>
              <p>📧 {invoice.sellerDetails.email}</p>
              {invoice.sellerDetails.gstin && <p>GSTIN: {invoice.sellerDetails.gstin}</p>}
            </div>

            <div className="info-card">
              <h3>Bill To: {invoice.buyerDetails.name || invoice.buyerDetails.businessName || 'Customer'}</h3>
              {invoice.buyerDetails.address?.street && <p>{invoice.buyerDetails.address.street}</p>}
              {(invoice.buyerDetails.address?.city || invoice.buyerDetails.address?.state || invoice.buyerDetails.address?.pincode) && (
                <p>
                  {[invoice.buyerDetails.address?.city, invoice.buyerDetails.address?.state, invoice.buyerDetails.address?.pincode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              <p>📞 {invoice.buyerDetails.phone}</p>
              <p>📧 {invoice.buyerDetails.email}</p>
              {invoice.buyerDetails.gstin && <p>GSTIN: {invoice.buyerDetails.gstin}</p>}
            </div>

            <div className="info-card">
              <h3>Invoice Details</h3>
              <p><strong>Invoice Date:</strong> {new Date(invoice.invoiceDate).toLocaleDateString()}</p>
              <p><strong>Due Date:</strong> {new Date(invoice.dueDate).toLocaleDateString()}</p>
              <p><strong>Place of Supply:</strong> {invoice.placeOfSupply}</p>
              {invoice.paymentDetails.status === 'paid' && (
                <>
                  <p><strong>Paid On:</strong> {new Date(invoice.paymentDetails.paidAt).toLocaleDateString()}</p>
                  <p><strong>Transaction ID:</strong> {invoice.paymentDetails.transactionId}</p>
                </>
              )}
            </div>
          </div>

          <div className="invoice-items">
            <h3>Items</h3>
            <table className="items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>HSN/SAC</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Tax</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{item.title}</strong>
                      {item.description && <p className="item-desc">{item.description}</p>}
                    </td>
                    <td>{item.hsnCode || item.sacCode}</td>
                    <td>{item.quantity} {item.unit}</td>
                    <td>₹{item.unitPrice.toFixed(2)}</td>
                    <td>₹{item.totalTax.toFixed(2)}</td>
                    <td>₹{item.totalAmount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="invoice-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>₹{invoice.summary.subtotal.toFixed(2)}</span>
            </div>
            {invoice.summary.totalDiscount > 0 && (
              <div className="summary-row">
                <span>Discount:</span>
                <span>-₹{invoice.summary.totalDiscount.toFixed(2)}</span>
              </div>
            )}
            {invoice.summary.cgstTotal > 0 && (
              <>
                <div className="summary-row">
                  <span>CGST:</span>
                  <span>₹{invoice.summary.cgstTotal.toFixed(2)}</span>
                </div>
                <div className="summary-row">
                  <span>SGST:</span>
                  <span>₹{invoice.summary.sgstTotal.toFixed(2)}</span>
                </div>
              </>
            )}
            {invoice.summary.igstTotal > 0 && (
              <div className="summary-row">
                <span>IGST:</span>
                <span>₹{invoice.summary.igstTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Grand Total:</span>
              <span>₹{invoice.summary.grandTotal.toFixed(2)}</span>
            </div>
          </div>

          {invoice.termsAndConditions && (
            <div className="terms">
              <h3>Terms and Conditions</h3>
              <p>{invoice.termsAndConditions}</p>
            </div>
          )}

          {invoice.notes && (
            <div className="notes">
              <h3>Notes</h3>
              <p>{invoice.notes}</p>
            </div>
          )}

          {invoice.downloadHistory && invoice.downloadHistory.length > 0 && (
            <div className="download-history">
              <h3>Download History</h3>
              <ul>
                {invoice.downloadHistory.map((download, index) => (
                  <li key={index}>
                    Downloaded on {new Date(download.downloadedAt).toLocaleString()}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {invoice.notificationsSent && invoice.notificationsSent.length > 0 && (
            <div className="notification-history">
              <h3>Notifications Sent</h3>
              <ul>
                {invoice.notificationsSent.map((notification, index) => (
                  <li key={index}>
                    <span className="notification-type">{notification.type.toUpperCase()}</span> - 
                    {notification.sentTo} - 
                    {new Date(notification.sentAt).toLocaleString()} - 
                    <span className={`notification-status ${notification.status}`}>
                      {notification.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default InvoiceDetail;
